import {
    Button,
    Col,
    Form,
    Input,
    Row,
    Select,
    message,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { useAppMutation } from "@/common/hooks/useAppMutation";
import { get, post } from "@/common/services/httpClient";
import { useCountryDropdown, useCurrencyDropdown } from "@/common/components/DynamicSelect";
import type { BankDetail } from "../../types/claimType";
import { useEffect } from "react";
import { useAppQuery } from "@/common/hooks/useAppQuery";
import type { DropdownOption } from "@/common/components/DynamicSelect";
import { useTranslation } from 'react-i18next';

/* =======================
   Helpers: NO SPACE INPUT
   ======================= */
const preventSpaceKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ' ') {
        e.preventDefault();
    }
};

const removeSpaces = (value?: string) =>
    value ? value.replace(/\s+/g, '') : value;

interface AddBankDetailFormProps {
    onCancel: () => void;
    policyHolderId: number;
    onSuccess: () => void;
    bankDetail?: BankDetail | null;
    cardNumber?: string;
}

const AddBankDetailForm = ({
    onCancel,
    policyHolderId,
    onSuccess,
    bankDetail,
    cardNumber
}: AddBankDetailFormProps) => {

    const [form] = useForm();
    const { t } = useTranslation();

    const { data: currenciesRaw, isLoading: loadingCurrencies } = useCurrencyDropdown();
    const { data: countriesRaw, isLoading: loadingCountries } = useCountryDropdown();

    const currencies = Array.isArray(currenciesRaw) ? currenciesRaw : [];
    const countries = Array.isArray(countriesRaw) ? countriesRaw : [];

    const selectedCountryId = Form.useWatch('countryId', form);

    const { data: cities, isLoading: loadingCities } = useAppQuery<DropdownOption[]>(
        ['citiesDropdown', selectedCountryId],
        async () => {
            if (!selectedCountryId) return [];
            const res = await get<{ data: { id: number; name: string }[] }>(
                `/City/SearchByCountryId?countryId=${selectedCountryId}`
            );
            return res.data?.map(item => ({
                label: item.name,
                value: item.id,
            })) ?? [];
        },
        { enabled: !!selectedCountryId }
    );

    useEffect(() => {
        if (bankDetail) {
            form.setFieldsValue(bankDetail);
        } else {
            form.resetFields();
        }
    }, [bankDetail, form]);

    const mutation = useAppMutation(
        async (values: BankDetail) => {
            const url =
                values.id && values.id > 0
                    ? "/Claim/UpdateBankDetail"
                    : "/Claim/SaveBankDetail";
            return post(url, values);
        },
        {
            onSuccess: (_data, variables) => {
                const isUpdate = variables.id && variables.id > 0;
                message.success(
                    isUpdate
                        ? t("Bank detail updated successfully!")
                        : t("Bank detail saved successfully!")
                );
                form.resetFields();
                onSuccess();
            },
            onError: (error: Error) => {
                message.error(`${t("Failed to save bank detail")}: ${error.message}`);
            },
        }
    );

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    const onFinish = (values: BankDetail) => {
        const currentCurrency = currencies.find((c: any) => c.value === values.currencyId);
        const currentCountry = countries.find((c: any) => c.value === values.countryId);
        const currentCity = cities?.find((c: any) => c.value === values.cityId);

        const payload: BankDetail = {
            ...values,
            policyHolderId,
            currencyISOCode: currentCurrency?.ISOCode ?? '',
            country: currentCountry?.label ?? '',
            city: currentCity?.label ?? '',
            cardNumber,
            iban: values.iban?.trim() || '',
        };

        if (bankDetail) {
            payload.id = bankDetail.id;
        }

        mutation.mutate(payload);
    };

    return (
        <div>
            <h2>{bankDetail ? t("Edit Bank Account") : t("Add New Bank Account")}</h2>

            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Row gutter={16}>

                    <Col span={12}>
                        <Form.Item
                            name="accountName"
                            label={t("Account Name")}
                            normalize={removeSpaces}
                            rules={[{ required: true, message: t("Required") }]}
                        >
                            <Input placeholder={t("Enter account name")} maxLength={150} />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="accountNo"
                            label={t("Account Number")}
                            normalize={removeSpaces}
                            rules={[
                                { required: true, message: t("Required") },
                                { pattern: /^\S+$/, message: t("Spaces are not allowed") }
                            ]}
                        >
                            <Input
                                placeholder={t("Enter account number")}
                                maxLength={34}
                                onKeyDown={preventSpaceKey}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="bankName"
                            label={t("Bank Name")}
                            normalize={removeSpaces}
                            rules={[{ required: true, message: t("Required") }]}
                        >
                            <Input placeholder={t("Enter bank name")} maxLength={140} />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="branch"
                            label={t("Branch")}
                            normalize={removeSpaces}
                            rules={[{ required: true, message: t("Required") }]}
                        >
                            <Input placeholder={t("Enter branch")} maxLength={150} />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="iban"
                            label={t("IBAN")}
                            normalize={removeSpaces}
                            rules={[
                                { pattern: /^[A-Za-z0-9]+$/, message: t("validation.alphaNumeric") }
                            ]}
                        >
                            <Input
                                placeholder={t("Enter IBAN")}
                                maxLength={34}
                                onKeyDown={preventSpaceKey}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="swift_BIC"
                            label={t("Swift/BIC")}
                            normalize={removeSpaces}
                            rules={[
                                { required: true, message: t("Required") },
                                { pattern: /^[A-Za-z0-9]+$/, message: t("validation.alphaNumeric") }
                            ]}
                        >
                            <Input
                                placeholder={t("Enter Swift/BIC")}
                                maxLength={11}
                                onKeyDown={preventSpaceKey}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item name="currencyId" label={t("Currency")} rules={[{ required: true, message: t("Required") }]}>
                            <Select
                                showSearch
                                placeholder={t("Select a currency")}
                                loading={loadingCurrencies}
                                options={currencies}
                                optionFilterProp="label"
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item name="countryId" label={t("Country")} rules={[{ required: true, message: t("Required") }]}>
                            <Select
                                showSearch
                                placeholder={t("Select a country")}
                                loading={loadingCountries}
                                options={countries}
                                optionFilterProp="label"
                                onChange={() => form.setFieldsValue({ cityId: undefined })}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item name="cityId" label={t("City")} rules={[{ required: true, message: t("Required") }]}>
                            <Select
                                showSearch
                                placeholder={t("Select a city")}
                                loading={loadingCities}
                                options={cities}
                                optionFilterProp="label"
                                disabled={!selectedCountryId}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item name="bankAddress" label={t("Bank Address")}
                            normalize={removeSpaces}
                            rules={[{ required: true, message: t("Required") }]}>
                            <Input placeholder={t("Enter bank address")} maxLength={300} />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="phone"
                            label={t("Phone")}
                            normalize={removeSpaces}
                            rules={[
                                { pattern: /^\S*$/, message: t("Spaces are not allowed") }
                            ]}
                        >
                            <Input
                                placeholder={t("Enter phone number")}
                                maxLength={20}
                                minLength={7}
                                onKeyDown={preventSpaceKey}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="email"
                            label={t("Email")}
                            normalize={removeSpaces}
                            rules={[
                                { type: "email", message: t("Invalid email") }
                            ]}
                        >
                            <Input
                                placeholder={t("Enter email")}
                                maxLength={254}
                                onKeyDown={preventSpaceKey}
                            />
                        </Form.Item>
                    </Col>

                </Row>

                <Form.Item>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                        <Button onClick={handleCancel}>{t("cancel")}</Button>
                        <Button type="primary" htmlType="submit" loading={mutation.isPending}>
                            {t("save")}
                        </Button>
                    </div>
                </Form.Item>
            </Form>
        </div>
    );
};

export default AddBankDetailForm;